import { FC } from 'react';
import { translate } from '../../i18n';
import { webPaymentStyles as styles } from './styles';

export type PaymentMethodsSkeletonVariant = 'list' | 'carousel-mobile';

export type PaymentMethodsCarouselInset = 'app' | 'web-mobile';

export interface PaymentMethodsSkeletonProps {
  variant?: PaymentMethodsSkeletonVariant;
  /** Ajuste de padding del carrusel mobile (app vs checkout web embebido). */
  carouselInset?: PaymentMethodsCarouselInset;
  /**
   * Mobile/app: al terminar el GET suele quedar la primera tarjeta preseleccionada y se muestran
   * CVV, “2 tarjetas” y cuotas debajo del carrusel.
   */
  withSelectedCardFlow?: boolean;
  testID?: string;
}

export type PaymentMethodsAddSkeletonVariant = 'desktop' | 'mobile';

/** Reserva el espacio del botón “Agregar tarjeta” mientras carga el listado. */
export const PaymentMethodsAddButtonSkeleton: FC<{ variant: PaymentMethodsAddSkeletonVariant }> = ({
  variant,
}) => (
  <span
    aria-hidden="true"
    className="payment-methods-skeleton__add"
    style={variant === 'desktop' ? styles.skeletonAddCardButton : styles.skeletonMobileAdd}
  />
);

const LIST_PLACEHOLDER_COUNT = 3;

const loadingStatusProps = {
  role: 'status' as const,
  'aria-live': 'polite' as const,
  'aria-busy': true,
  'aria-label': translate('paymentsUI.common.loading'),
};

const MobileSelectedCardFlowSkeleton: FC = () => (
  <div className="payment-methods-skeleton__selected-flow" style={styles.skeletonMobileSelectedFlow} aria-hidden="true">
    <span style={{ ...styles.skeletonMobileLine, width: 140 }} />
    <span style={styles.skeletonMobileCvvField} />
    <span style={styles.skeletonMobileToggle} />
    <span style={{ ...styles.skeletonMobileLine, width: 220, marginTop: 4 }} />
    <span style={styles.skeletonMobileInstallmentRow} />
    <span style={styles.skeletonMobileInstallmentRow} />
  </div>
);

/** Carrusel mobile (app + web `layout=mobile`): tarjeta ancha con asomo de la siguiente. */
const MobileCarouselSkeleton: FC<{
  testID: string;
  inset: PaymentMethodsCarouselInset;
  withSelectedCardFlow: boolean;
}> = ({ testID, inset, withSelectedCardFlow }) => (
  <div
    {...loadingStatusProps}
    data-testid={testID}
    className="payment-methods-skeleton payment-methods-skeleton--mobile-carousel"
    style={styles.skeletonMobileCarouselRoot}
  >
    <div style={styles.skeletonMobileCarouselContainer}>
      <div
        style={{
          ...styles.skeletonMobileCarouselTrack,
          ...(withSelectedCardFlow ? styles.skeletonMobileCarouselTrackPreselected : undefined),
          ...(inset === 'app' ? styles.skeletonMobileCarouselTrackApp : styles.skeletonMobileCarouselTrackWeb),
        }}
      >
        <div
          className="payment-methods-skeleton__card"
          style={{
            ...styles.skeletonMobileCarouselCardBase,
            ...(withSelectedCardFlow
              ? styles.skeletonMobileCarouselCardPreselected
              : styles.skeletonMobileCarouselCardNeutral),
          }}
        />
        <div className="payment-methods-skeleton__peek" style={styles.skeletonMobileCarouselPeek} aria-hidden="true" />
      </div>
    </div>
    {withSelectedCardFlow && <MobileSelectedCardFlowSkeleton />}
  </div>
);

export const PaymentMethodsSkeleton: FC<PaymentMethodsSkeletonProps> = ({
  variant = 'list',
  carouselInset = 'web-mobile',
  withSelectedCardFlow = true,
  testID = 'payment-methods-skeleton',
}) => {
  if (variant === 'carousel-mobile') {
    return (
      <MobileCarouselSkeleton
        testID={testID}
        inset={carouselInset}
        withSelectedCardFlow={withSelectedCardFlow}
      />
    );
  }

  return (
    <ul
      {...loadingStatusProps}
      data-testid={testID}
      className="payment-methods-skeleton payment-methods-skeleton--list"
      style={styles.list}
    >
      {Array.from({ length: LIST_PLACEHOLDER_COUNT }, (_, index) => (
        <li key={index}>
          <div className="payment-methods-skeleton__row" style={styles.skeletonRow}>
            <span className="payment-methods-skeleton__block" style={styles.skeletonCircle} />
            <span className="payment-methods-skeleton__block" style={styles.skeletonBrand} />
            <span style={styles.skeletonTextCol}>
              <span className="payment-methods-skeleton__block" style={styles.skeletonLinePrimary} />
              <span className="payment-methods-skeleton__block" style={styles.skeletonLineSecondary} />
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
};
